'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { MergeabilityStatusResponse } from '@/lib/hooks/mergeability-status/query-data';
import type { PrV2MergeMethod } from '@/lib/pr-v2/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { setPrV2AutoMerge } from './fetch-pr-v2';
import { recomputePrV2MergeabilityStatus, rollbackQueryData } from './mergeability-utils';
import { prV2SummaryQueryKey } from './query-keys';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { mergeabilityStatusQueryKey } from '@/lib/hooks/mergeability-status/keys';
import { usePrStore } from '@/lib/stores/pr-store';
type AutoMergeVariables = {
  action: 'enable';
  pullRequestId: string;
  mergeMethod: PrV2MergeMethod;
} | {
  action: 'disable';
  pullRequestId: string;
  mergeMethod: PrV2MergeMethod;
};
const ENABLED_METHOD_BY_MERGE_METHOD: Record<PrV2MergeMethod, NonNullable<MergeabilityStatus['autoMerge']['enabledMethod']>> = {
  merge: 'MERGE',
  squash: 'SQUASH',
  rebase: 'REBASE'
};
export function usePrV2AutoMergeActions(owner, repo, number) {
  const $ = _c(36);
  const queryClient = useQueryClient();
  const {
    installationId
  } = usePrStore();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2SummaryQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const summaryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = mergeabilityStatusQueryKey(owner, repo, number, "v2");
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const mergeabilityKey = t1;
  let t2;
  if ($[8] !== number || $[9] !== owner || $[10] !== repo) {
    t2 = async variables => {
      const promise = setPrV2AutoMerge(owner, repo, number, variables);
      toast.promise(promise, {
        loading: variables.action === "enable" ? "Enabling auto-merge..." : "Disabling auto-merge...",
        success: variables.action === "enable" ? "Auto-merge enabled" : "Auto-merge disabled",
        error: _temp
      });
      return promise;
    };
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== mergeabilityKey || $[13] !== queryClient) {
    t3 = async variables_0 => {
      await queryClient.cancelQueries({
        queryKey: mergeabilityKey
      });
      const previousMergeability = queryClient.getQueryData(mergeabilityKey);
      queryClient.setQueryData(mergeabilityKey, current => recomputePrV2MergeabilityStatus(current, status => ({
        ...status,
        autoMerge: {
          ...status.autoMerge,
          alreadyEnabled: variables_0.action === "enable",
          enabledMethod: variables_0.action === "enable" ? ENABLED_METHOD_BY_MERGE_METHOD[variables_0.mergeMethod] : null
        }
      })));
      return {
        previousMergeability
      };
    };
    $[12] = mergeabilityKey;
    $[13] = queryClient;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== installationId || $[16] !== number || $[17] !== owner || $[18] !== repo) {
    t4 = (_data, variables_1) => {
      const eventName = variables_1.action === "enable" ? "v2_pr_auto_merge_enabled" : "v2_pr_auto_merge_disabled";
      trackClientEvent(eventName, installationId?.toString(), {
        owner,
        repo,
        pr_number: number,
        merge_method: variables_1.mergeMethod
      });
    };
    $[15] = installationId;
    $[16] = number;
    $[17] = owner;
    $[18] = repo;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== mergeabilityKey || $[21] !== queryClient) {
    t5 = (_error, _variables, context) => {
      if (!context) {
        return;
      }
      rollbackQueryData(queryClient, mergeabilityKey, context.previousMergeability);
    };
    $[20] = mergeabilityKey;
    $[21] = queryClient;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== mergeabilityKey || $[24] !== queryClient || $[25] !== summaryKey) {
    t6 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: summaryKey
      }), queryClient.invalidateQueries({
        queryKey: mergeabilityKey
      })]);
    };
    $[23] = mergeabilityKey;
    $[24] = queryClient;
    $[25] = summaryKey;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t2 || $[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t7 = {
      mutationFn: t2,
      onMutate: t3,
      onSuccess: t4,
      onError: t5,
      onSettled: t6
    };
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const mutation = useMutation(t7);
  let t8;
  if ($[33] !== mutation.isPending || $[34] !== mutation.mutateAsync) {
    t8 = {
      setAutoMerge: mutation.mutateAsync,
      isPending: mutation.isPending
    };
    $[33] = mutation.isPending;
    $[34] = mutation.mutateAsync;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
}
function _temp(error) {
  return error instanceof Error ? error.message : "Failed to update auto-merge";
}